'use client';

import { FONT_TEXT } from '@/app/fonts';
import { ShareableChart } from '@/components/blog/ShareableChart';

const milestones = [
  {
    period: 'Month 1',
    dbSize: '50',
    monthlyFromDB: '0',
    note: 'Leads start entering your database. Exclusively yours — no vendor, no algorithm, no platform update can take them.',
    color: '#294bfd',
    bgColor: '#f0f4ff',
    borderColor: '#294bfd',
    dim: true,
  },
  {
    period: 'Month 6',
    dbSize: '300',
    monthlyFromDB: '1–2',
    note: 'Early nurture sequences convert leads who weren\'t ready in Month 1. They\'re ready now — and they cost you nothing extra.',
    color: '#294bfd',
    bgColor: '#e8eeff',
    borderColor: '#294bfd',
    dim: false,
  },
  {
    period: 'Month 12',
    dbSize: '600',
    monthlyFromDB: '3–5',
    note: 'Database reactivation becomes a reliable monthly revenue stream alongside your paid pipeline.',
    color: '#ff34a7',
    bgColor: '#fff5fb',
    borderColor: '#ff34a7',
    dim: false,
  },
  {
    period: 'Year 3',
    dbSize: '5,000+',
    monthlyFromDB: '10–15',
    note: 'Self-sustaining pipeline. Organic content compounds. Referrals compound. The database does the heavy lifting.',
    color: '#10b981',
    bgColor: '#f0fdf4',
    borderColor: '#10b981',
    dim: false,
  },
];

export function FirstPartyDataValue() {
  return (
    <ShareableChart title="First-Party Database Value - Compounding Over Time">
      <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: FONT_TEXT }}>
        Your Database Compounds. Third-Party Lists Don't.
      </h3>
      <p className="text-sm text-gray-500 mb-6" style={{ fontFamily: FONT_TEXT }}>
        Every first-party lead you generate stays in your system permanently — market to them forever at near-zero marginal cost
      </p>

      {/* Timeline progression */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {milestones.map((m, i) => (
          <div
            key={m.period}
            className="rounded-lg border-2 p-4 flex flex-col gap-2"
            style={{ borderColor: m.borderColor, backgroundColor: m.bgColor }}
          >
            {/* Arrow connector (desktop only) */}
            <div className="flex items-center justify-between mb-1">
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                style={{ backgroundColor: m.color, fontFamily: FONT_TEXT }}
              >
                {m.period}
              </span>
              {i < milestones.length - 1 && (
                <span className="text-gray-300 text-lg hidden lg:block">→</span>
              )}
            </div>

            {/* Metrics */}
            <div style={{ fontFamily: FONT_TEXT }}>
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-xs text-gray-500">Database size</span>
                <span className="text-sm font-bold text-gray-900">{m.dbSize} leads</span>
              </div>
              <div className="flex justify-between items-baseline border-t pt-2" style={{ borderColor: m.borderColor }}>
                <span className="text-xs text-gray-500">Loans/mo from DB</span>
                <span
                  className="text-sm font-bold"
                  style={{ color: m.monthlyFromDB === '0' ? '#9ca3af' : m.color }}
                >
                  {m.monthlyFromDB === '0' ? 'Building' : m.monthlyFromDB}
                </span>
              </div>
            </div>

            {/* Note */}
            <p className="text-xs text-gray-600 mt-1 leading-relaxed" style={{ fontFamily: FONT_TEXT }}>
              {m.note}
            </p>
          </div>
        ))}
      </div>

      {/* Core insight callouts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="p-3 rounded-lg border-l-4" style={{ borderColor: '#ff34a7', backgroundColor: '#fff5fb' }}>
          <p className="text-sm font-semibold text-gray-900 mb-1" style={{ fontFamily: FONT_TEXT }}>
            "Not ready now" ≠ "never ready"
          </p>
          <p className="text-xs text-gray-600" style={{ fontFamily: FONT_TEXT }}>
            Leads you can't close today close in 6–18 months — at near-zero cost. Your competitors discard these. You nurture them.
          </p>
        </div>
        <div className="p-3 rounded-lg border-l-4" style={{ borderColor: '#294bfd', backgroundColor: '#f0f4ff' }}>
          <p className="text-sm font-semibold text-gray-900 mb-1" style={{ fontFamily: FONT_TEXT }}>
            No algorithm can take this away
          </p>
          <p className="text-xs text-gray-600" style={{ fontFamily: FONT_TEXT }}>
            iOS changes, cookie deprecation, platform policy updates — none of it touches a first-party list you own. This is the only lead source that's truly yours.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center pt-2 border-t border-gray-100">
        <p className="text-sm text-gray-600 mb-1" style={{ fontFamily: FONT_TEXT }}>
          Every market is different. See how these numbers look for your specific business:
        </p>
        <a
          href="/mortgage-marketing-roi-calculator"
          className="inline-block text-sm font-semibold px-4 py-2 rounded-lg text-white mt-1"
          style={{ backgroundColor: '#ff34a7', fontFamily: FONT_TEXT }}
        >
          Run Your Numbers →
        </a>
      </div>

      <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: FONT_TEXT }}>
        Source: leadpops.com · Results vary by market, nurture systems, and follow-up consistency
      </p>
    </ShareableChart>
  );
}
